import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook'
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons/faCircleInfo'
import EmptyState from 'components/reusables/emptyState'
import ProfilesCountCell from 'components/reusables/profilesCountCell'
import RestrictedResourcesModal from 'components/reusables/restrictedResourcesModal'
import { dc3AttestationsService } from 'api/administration/dc3Attestations'
import { IDc3AssociatedCourse } from 'interfaces/dc3Attestation'

interface IProps {
  attestationId: number
  onClose: () => void
}

const AssociatedCoursesModal = ({ attestationId, onClose }: IProps) => {
  const { t } = useTranslation()
  const translationPrefix =
    'administration.certificate.legalAttestationsList.associatedCoursesModal'

  const fetchData = useCallback(
    async ({ page, search, perPage }) => {
      const response = await dc3AttestationsService.searchAssociatedCourses(
        attestationId,
        { page, search, perPage }
      )

      return {
        allowedItems: response.data.allowedCourses,
        restrictedCount: response.data.restrictedCoursesCount,
        total: response.data.total,
      }
    },
    [attestationId]
  )

  const customEmpty = useMemo(
    () => (
      <EmptyState title={t(`${translationPrefix}.searchEmpty`)} icon={faBook} />
    ),
    [t, translationPrefix]
  )

  return (
    <RestrictedResourcesModal<IDc3AssociatedCourse>
      title={t(`${translationPrefix}.title`)}
      close={onClose}
      fetchData={fetchData}
      columns={[
        {
          title: t(`${translationPrefix}.columns.course`),
          key: 'name',
          dataIndex: 'name',
          render: (_name: unknown, row: IDc3AssociatedCourse) => (
            <div>
              {row.color === 'unset' && <FontAwesomeIcon icon={faCircleInfo} />}
              {row.name}
            </div>
          ),
        },
        {
          title: t(`${translationPrefix}.columns.profiles`),
          key: 'lmsProfiles',
          dataIndex: 'lmsProfiles',
          render: (_lmsProfiles: unknown, row: IDc3AssociatedCourse) =>
            row.color === 'unset' ? null : (
              <ProfilesCountCell
                resourceId={row.id}
                resourceType="course"
                count={row.lmsProfiles?.length || 0}
              />
            ),
        },
      ]}
      countIcon={faBook}
      locale={{
        rowsCount: t(`${translationPrefix}.rowsCount`),
        rowsCount_plural: t(`${translationPrefix}.rowsCount_plural`),
        search: t('administration.courses.list.search'),
      }}
      closeButtonText={t(`${translationPrefix}.close`)}
      restrictedRowLabel={(count) =>
        t(`${translationPrefix}.restrictedCourse`, { count })
      }
      alertConfig={{
        translationPrefix,
        resourceTypeLabel: t(`${translationPrefix}.resourceType`),
        totalItemKey: 'course',
        restrictedItemKey: 'restrictedCourse',
        totalValueKey: 'totalCourses',
        restrictedValueKey: 'restrictedCourses',
        showAdditionalMessage: false,
      }}
      customEmpty={customEmpty}
    />
  )
}

export default AssociatedCoursesModal
